import clsx from "clsx";
import { ReactNode, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMatch, useSearchParams } from "react-router-dom";

import { Filter, Search } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";

import { FilterDialog } from "./FilterDialog/FilterDialog";
import { SearchBar } from "./SearchBar/SearchBar";
import { FilterDialogForm } from "./types";
import {
  FILTER_PARAM_KEYS,
  getFilterAndSearchParams,
  getFormStateFromSearchParams,
} from "./utils";

interface PortalDeviceListFilterProps {
  /** The route used for the device list view */
  listRoute: string;
  /**
   * Tiles/table switch, rendered at the trailing end of the same toolbar row
   * as the search and filter buttons. Falsy hides it.
   */
  viewToggle: ReactNode;
  /**
   * Search and filter only make sense once there's more than one
   * subscription to search or filter through - with 0 or 1, hide the search
   * bar and the filter button, independently of the view toggle.
   */
  showSearchAndFilter: boolean;
}

/**
 * Main component for filtering the portal's device list.
 * Includes a search bar and a detailed filter dialog.
 */
export const PortalDeviceListFilter = ({
  listRoute,
  viewToggle,
  showSearchAndFilter,
}: PortalDeviceListFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isListView = useMatch(listRoute);
  const { t } = useTranslation("portal");

  const { watch, control, setValue, register, reset } =
    useForm<FilterDialogForm>({
      defaultValues: getFormStateFromSearchParams(searchParams),
    });

  const formState = watch();

  /**
   * Removes all filter-related URL parameters.
   *
   * @param searchParams - The current URLSearchParams.
   * @returns The updated query string.
   */
  const removeURLParams = (searchParams: URLSearchParams) => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    FILTER_PARAM_KEYS.forEach((key) => updatedSearchParams.delete(key));

    return updatedSearchParams.toString();
  };

  /**
   * Closes the filter modal and updates the URL search parameters based on the current form state.
   */
  const closeModal = () => {
    const updatedSearchParams = getFilterAndSearchParams(
      formState,
      searchParams,
    ).toString();
    setSearchParams(updatedSearchParams);
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const resetFilters = () => {
    const resettedSearchParams = removeURLParams(searchParams);
    reset();
    setSearchParams(resettedSearchParams);
    setIsOpen(false);
  };

  const hasFormFields =
    formState.simVoiceOption !== "ALL" ||
    formState.simAutoRenew !== "ALL" ||
    formState.simStatus.length > 0 ||
    formState.simType.length > 0 ||
    formState.additionalColumns.length > 0;

  // The applied state is whatever is currently persisted in the URL.
  const appliedFormState = getFormStateFromSearchParams(searchParams);

  const hasAppliedFilters =
    appliedFormState.simVoiceOption !== "ALL" ||
    appliedFormState.simAutoRenew !== "ALL" ||
    appliedFormState.simStatus.length > 0 ||
    appliedFormState.simType.length > 0 ||
    appliedFormState.additionalColumns.length > 0;

  // `showSearchAndFilter` hides these controls once the customer's
  // unfiltered total drops to 0 or 1 - but an active searchTerm/filter can
  // still be sitting in the URL from before (e.g. a subscription got
  // cancelled after the search was applied, or a shared/bookmarked link).
  // Hiding the only way to clear that would strand the customer on a
  // filtered, possibly empty view. Keep the controls reachable whenever
  // there's URL state to clear, regardless of the count-based visibility.
  const hasActiveSearchOrFilterParams =
    !!searchParams.get("searchTerm") || hasAppliedFilters;
  const showControls = showSearchAndFilter || hasActiveSearchOrFilterParams;

  const searchInputRef = useRef<HTMLInputElement>(null);

  /**
   * Focuses the search input field.
   * To make it more user-friendly, we want to enable typing once the search-field is opened.
   */
  const focusSearchInput = () => {
    searchInputRef.current?.focus();
  };

  const blurSearchInput = () => {
    searchInputRef.current?.blur();
  };

  const toggleSearch = () => {
    setSearchIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        // Needed to fix a race-condition when closing the search bar via button click
        setTimeout(() => focusSearchInput(), 0);
      } else {
        blurSearchInput();
      }

      return newState;
    });
  };

  const handleSearchToggleMouseDown = (e: React.MouseEvent) => {
    // Verhindert, dass der Toggle-Button den Fokus stiehlt, was ein onBlur auf dem Input auslösen würde.
    // Dies verhindert einen Race-Condition-Effekt, bei dem onBlur die Bar schließt, bevor onClick sie wieder öffnet.
    e.preventDefault();
  };

  // Without controls or toggle the row would still reserve its min-height.
  if (!showControls && !viewToggle) {
    return null;
  }

  return (
    <div className="flex flex-col gap-gutter tablet:min-h-14 tablet:flex-row tablet:items-center tablet:gap-4">
      {showControls && (
        <SearchBar
          className={clsx(
            !searchIsOpen && "tablet:hidden",
            "tablet:min-w-0 tablet:flex-1 desktop:w-1/2 desktop:flex-none",
          )}
          onEmptyBlur={() => setSearchIsOpen(false)}
          inputRef={searchInputRef}
        />
      )}
      <div
        className={clsx(
          "flex items-center gap-4 tablet:flex-1",
          showControls ? "justify-between" : "justify-end",
        )}
      >
        {showControls && (
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                "hidden tablet:block",
                searchIsOpen && "tablet:hidden",
              )}
            >
              <Button
                ariaLabel={t("cockpit.filter.searchBar.toggle")}
                variant="tertiary"
                icon={<Search />}
                pressed={searchIsOpen}
                onClick={toggleSearch}
                onMouseDown={handleSearchToggleMouseDown}
              />
            </div>
            <div className="relative">
              <Button
                ariaLabel={t("cockpit.filter.openFilters")}
                variant="tertiary"
                icon={<Filter />}
                pressed={isOpen}
                onClick={openModal}
              />
              {hasFormFields && (
                <span
                  data-testid="active-filters-indicator"
                  aria-hidden="true"
                  className="pointer-events-none absolute right-1 top-1 size-2 rounded-full bg-surface-primary"
                />
              )}
            </div>
          </div>
        )}
        {viewToggle}
      </div>
      {showControls && (
        <FilterDialog
          isOpen={isOpen}
          onClose={closeModal}
          onReset={resetFilters}
          isListView={!!isListView}
          watch={watch}
          setValue={setValue}
          register={register}
          control={control}
          hasFormFields={hasFormFields}
          hasAppliedFilters={hasAppliedFilters}
        />
      )}
    </div>
  );
};
